import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { TwoFactorService } from '../core/services/two-factor.service';
import { TwoFactorStore } from '../core/state/two-factor.store';
import { isImpersonatedSession } from '../services/auth.service';

export const TWO_FACTOR_REQUIRED_ROUTE = '/two-factor-required';

/**
 * Blocks the whole portal until the client has two-factor authentication enabled.
 * Un-enrolled clients are sent to the full-screen enrolment page.
 */
export const twoFactorRequiredGuard: CanActivateChildFn = (): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const router = inject(Router);
  const store = inject(TwoFactorStore);
  const twoFactorService = inject(TwoFactorService);

  // Admins impersonating a client keep access even if that client has not enrolled.
  if (isImpersonatedSession()) {
    return true;
  }

  const lockdownUrl = () => router.createUrlTree([TWO_FACTOR_REQUIRED_ROUTE]);

  const cached = store.status();
  if (cached) {
    return cached.enabled ? true : lockdownUrl();
  }

  return twoFactorService.getStatus().pipe(
    map((status) => (status.enabled ? true : lockdownUrl())),
    catchError((err: HttpErrorResponse) => {
      // TokenInterceptor already logged out and redirected to the login page on a 401,
      // so returning a UrlTree here would race that navigation.
      if (err.status === 401) {
        return of(false);
      }
      // Any other failure means we cannot prove 2FA is on, so keep the portal closed.
      return of(lockdownUrl());
    })
  );
};

/**
 * Keeps the enrolment page out of the way of clients who already have 2FA on.
 * Reads the cached status only, the page loads its own status on init.
 */
export const twoFactorEnrolmentPageGuard: CanActivateFn = (): boolean | UrlTree => {
  const router = inject(Router);
  const store = inject(TwoFactorStore);

  if (store.status()?.enabled) {
    return router.createUrlTree(['/dashboard']);
  }

  return true;
};
